import { type ActionsObject, makeAction, makeReducer } from '@finn-no/redux-actions';
import type { FilterType, ResetCurrentFilterPayload } from '../components/filters/currentFilters/FilterToFilterData';

import type { CheckboxFilters, CheckboxMap, CheckboxTree, Filters, Month, NumberRange } from '../interfaces';
import defaultState from './defaultState';

export const actions: ActionsObject<Filters> = {};

const toggleCheckbox = (checkboxes: CheckboxMap, value: string): CheckboxMap => {
    const { [value]: toggledOn, ...rest } = checkboxes;
    return toggledOn ? rest : { ...rest, [value]: true };
};

// Recursive function that follows a path in a nested CheckboxTree, and then toggles the last element
const toggleNestedCheckbox = (checkboxes: CheckboxTree, path: string[]): CheckboxTree => {
    const [head, ...rest] = path;
    if (rest.length <= 0) {
        const { [head]: toggledOn, ...final } = checkboxes;
        return toggledOn ? final : { ...final, [head]: {} };
    }
    return { ...checkboxes, [head]: toggleNestedCheckbox(checkboxes[head], rest) };
};

const toggleMonth = (months: Month[], month: number, year: number): Month[] => {
    const copy = [...months];
    const index = copy.findIndex((e: Month) => e.month === month && e.year === year);
    if (index >= 0) {
        copy.splice(index, 1);
    } else {
        copy.push({ month, year });
    }
    return copy;
};

const addValueToList = (values: string[], value: string): string[] => {
    const copy = [...values];
    const index = copy.indexOf(value);
    if (index < 0) {
        copy.push(value);
    }
    return copy;
};

const removeValueFromList = (values: string[], value: string): string[] => {
    const copy = [...values];
    const index = copy.indexOf(value);
    if (index >= 0) {
        copy.splice(index, 1);
    }
    return copy;
};

actions.filter = makeAction('SET_FILTERS', (state, action) => ({ ...state, ...action.payload }));

actions.resetFilters = makeAction('RESET_FILTERS', () => ({ ...defaultState.filters }));

actions.resetHotelFilters = makeAction('RESET_HOTEL_FILTERS', (state) => ({
    ...defaultState.filters,
    hotelId: state.hotelId,
}));

actions.changeSort = makeAction('CHANGE_SORT', (state, action) => ({ ...state, sort: action.payload, page: undefined }));

actions.changePage = makeAction('CHANGE_PAGE', (state, action) => ({ ...state, page: action.payload }));

actions.changeOrigin = makeAction('CHANGE_ORIGIN', (state, action) => ({ ...state, origin: action.payload, page: undefined }));

actions.addDestination = makeAction('ADD_DESTINATION', (state, action) => ({
    ...state,
    destinations: {
        list: addValueToList(state.destinations.list, action.payload),
        tree: {},
    },
    page: undefined,
}));

actions.removeDestination = makeAction('REMOVE_DESTINATION', (state, action) => ({
    ...state,
    destinations: {
        list: removeValueFromList(state.destinations.list, action.payload),
        tree: {},
    },
    page: undefined,
}));

actions.toggleTravelMonth = makeAction('TOGGLE_TRAVEL_MONTH', (state, action) => ({
    ...state,
    travelMonths: toggleMonth(state.travelMonths, action.payload.month, action.payload.year),
    page: undefined,
}));

actions.resetTravelMonths = makeAction('RESET_TRAVEL_MONTHS', (state) => ({ ...state, travelMonths: [], page: undefined }));

actions.changeDepartureDate = makeAction('CHANGE_DEPARTURE_DATE', (state, action) => ({
    ...state,
    departureDate: { ...state.departureDate, date: action.payload },
    page: undefined,
}));

actions.changeReturnDate = makeAction('CHANGE_RETURN_DATE', (state, action) => ({
    ...state,
    returnDate: { ...state.returnDate, date: action.payload },
    page: undefined,
}));

actions.changeFlexibleDeparture = makeAction('CHANGE_FLEXIBLE_DEPARTURE', (state, action) => ({
    ...state,
    departureDate: { ...state.departureDate, flexibility: action.payload },
    page: undefined,
}));

actions.changeFlexibleReturn = makeAction('CHANGE_FLEXIBLE_RETURN', (state, action) => ({
    ...state,
    returnDate: { ...state.returnDate, flexibility: action.payload },
    page: undefined,
}));

actions.changeDuration = makeAction('CHANGE_DURATION', (state, action) => ({ ...state, duration: action.payload, page: undefined }));

actions.setIgnoredFilters = makeAction('SET_IGNORED_FILTERS', (state, action) => ({
    ...state,
    ignoredFilters: action.payload,
    page: undefined,
}));

actions.filterCheckboxDestination = makeAction('FILTER_CHECKBOX_DESTINATION', (state, action) => ({
    ...state,
    destinations: {
        list: [],
        tree: toggleNestedCheckbox(state.destinations.tree, action.payload),
        // tree: {},
    },
    page: undefined,
}));

actions.filterResetSpecific = makeAction('FILTER_RESET_SPECIFIC', (state, action) => {
    const payload: ResetCurrentFilterPayload = action.payload;

    const filterChange: Record<FilterType, () => Partial<Filters>> = {
        checkboxMap: () => ({
            [payload.location]: toggleCheckbox(state[payload.location as CheckboxFilters], action.payload.value),
        }),
        nestedCheckboxMap: () => ({
            [payload.location]: { list: [], tree: toggleNestedCheckbox(state.destinations.tree, action.payload.value) },
        }),
        dateSpecific: () => ({ departureDate: defaultState.filters.departureDate, returnDate: defaultState.filters.returnDate }),
        dateMonth: () => ({ travelMonths: defaultState.filters.travelMonths, duration: defaultState.filters.duration }),
        simple: () => ({ [payload.location]: defaultState.filters[payload.location as keyof Filters] }),
    };

    return {
        ...state,
        ...filterChange[payload.filterType](),
        page: undefined,
    };
});

actions.filterCheckboxMeal = makeAction('FILTER_CHECKBOX_MEAL', (state, action) => ({
    ...state,
    meals: toggleCheckbox(state.meals, action.payload),
    page: undefined,
}));

actions.filterCheckboxFacility = makeAction('FILTER_CHECKBOX_FACILITY', (state, action) => ({
    ...state,
    facilities: toggleCheckbox(state.facilities, action.payload),
    page: undefined,
}));

actions.filterCheckboxActivity = makeAction('FILTER_CHECKBOX_ACTIVITY', (state, action) => ({
    ...state,
    activities: toggleCheckbox(state.activities, action.payload),
    page: undefined,
}));

actions.filterCheckboxSupplier = makeAction('FILTER_CHECKBOX_SUPPLIER', (state, action) => ({
    ...state,
    suppliers: toggleCheckbox(state.suppliers, action.payload),
    page: undefined,
}));

actions.filterCheckboxRating = makeAction('FILTER_CHECKBOX_RATING', (state, action) => ({
    ...state,
    rating: toggleCheckbox(state.rating, action.payload),
    page: undefined,
}));

actions.filterPriceRange = makeAction('FILTER_PRICE_RANGE', (state, action) => ({
    ...state,
    priceRange: action.payload,
    page: undefined,
}));

actions.filterDistanceToBeach = makeAction('FILTER_DISTANCE_TO_BEACH', (state, action) => ({
    ...state,
    distanceToBeach: action.payload ? ([0, action.payload] as NumberRange) : undefined,
    page: undefined,
}));

actions.filterDistanceToCity = makeAction('FILTER_DISTANCE_TO_CITY', (state, action) => ({
    ...state,
    distanceToCity: action.payload ? ([0, action.payload] as NumberRange) : undefined,
    page: undefined,
}));

actions.filterByKeywords = makeAction('FILTER_BY_KEYWORDS', (state, action) => ({
    ...state,
    keywords: action.payload,
    page: undefined,
}));

export const filterReducer = makeReducer<Filters>(actions);
